import React from 'react';

import { FormInstance } from 'antd/lib/form';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';

import { ILoginState, ICarLocationsState, ICarsDatesState, getCarCrossSellLocations } from '@share/store/slices';
import {
  getCarsDetails,
  ICarsDetailsState,
  ICarsReviewBookState,
  resetCreditCard,
  setSelectedCarsReviewClientCash,
  setSelectedCarsReviewClientCashStr,
  carsDetailsActions,
  setCarsErrorStripe,
} from '@store/slices';
import {
  getCarIdFromPath,
  RootState,
  runStripeExpressResumeFromQuery,
  mergeStripeExpressResumeSearch,
  computeCreditLineChargeTotal,
  isCreditLineCheckoutAccount,
  redirectToCheckoutFromStripeResume,
  consumeStripeResumeErrorFromLocation,
} from '@share/utils';
import { store } from '@store';
import { CreditLineCheckoutPageBanner, StripeResumeError } from '@share/components';
import { UrlUtils } from '@share/utils';
import { CARS_SESSION_KEY_LABEL, DEAL_HOME_LABEL, DEALID_LABEL, QUOTE_LABEL, CROSS_SELL_AT_CHECKOUT } from '@share/constants';
import { BookingErrorsEnum, ISessionKey } from '@share/common-types';
import { AvailabilityStatusModal, GeneralMessage, MessageType, SkeletonReviewBook, CrossSellSection } from '@components';
import { CustomErrorPage } from '@share/components';

import { CarsDetailsReturn } from '../cars-details-return';
import { CarsDetailsServerError } from '../cars-details-server-error';
import { CarsPriceDetails } from '../cars-price-details';
import { CarsBookingConfirmed } from '../cars-booking-confirmed';
import { CarsDetails } from '../cars-details/component';

import './style.scss';
import { CarsLocations } from '../cars-location';
import GlobalLoading from '../../../../../share/components/global-spinner/component';

interface IMapStateToProps {
  carsDetailsStore: ICarsDetailsState;
  carsReviewBookStore: ICarsReviewBookState;
  carsLocationsStore: ICarLocationsState;
  carsDatesStore: ICarsDatesState;

  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  getCarsDetails: (numberId: string, sessionKey: ISessionKey | undefined, dealId: number, quote: string) => void;
  setSelectedCarsReviewClientCash: (clientCash: number) => void;
  setSelectedCarsReviewClientCashStr: (clientCash: string) => void;
  setCarsErrorStripe: (message: string) => void;
  resetCreditCard: () => void;
}

interface IParams {
  id: string;
}

export interface ButtonStripeComplete {
  driverForm: boolean,
  billingForm: boolean,
  emailForm: boolean
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps<IParams> {
  isStripe?: boolean;
}

interface IState {
  dealId: number;
  backHomeType: string;
  isCheckout: boolean;
  stripeResumeError: string | null;
  stripeNoResume: boolean;
}

class CarsDetailsWrapperComponent extends React.Component<IProps, IState> {
  state: IState = {
    dealId: null,
    backHomeType: null,
    isCheckout: false,
    stripeResumeError: null,
    stripeNoResume: false,
  };

  /** Error message piped through `history.state` when the /stripe/cars page redirected here on
   * resume failure. Kept on the instance so it can be re-applied after every `getDetails()` call
   * (the cars-details thunk calls `resetCarsState()` synchronously, which would otherwise clear it). */
  pendingStripeResumeError: string | null = null;

  overviewRef: React.RefObject<HTMLDivElement> = React.createRef();
  paymentRef: React.RefObject<HTMLDivElement> = React.createRef();
  driverRef: React.RefObject<HTMLDivElement> = React.createRef();
  emailConfirmationRef: React.RefObject<HTMLDivElement> = React.createRef();
  reserveRef: React.RefObject<HTMLDivElement> = React.createRef();
  locationRef: React.RefObject<HTMLDivElement> = React.createRef();
  policiesRef: React.RefObject<HTMLDivElement> = React.createRef();
  extrasRef: React.RefObject<HTMLDivElement> = React.createRef();

  paymentFormRef = React.createRef<FormInstance>();
  driverFormRef = React.createRef<FormInstance>();
  emailFormRef = React.createRef<FormInstance>();
  billingFormRef = React.createRef<FormInstance>();

  componentDidMount() {
    if (this.props.isStripe) {
      this.runStripeResume();
      return;
    }
    this.initNonStripe();
  }

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    const { user } = this.props.loginStore;
    if (!this.props.isStripe && !prevProps.loginStore.user && !!user) {
      this.getDetails();
      this.applyPendingStripeResumeError();
    }

    // React Router (v5 + <Switch>) reuses the same `CarsDetailPage` instance across the
    // `/stripe/car/:id` ⇄ `/cars/details/:id` transition (same component type, no key), so
    // componentDidMount doesn't re-run after `redirectToCheckoutFromStripeResume`. Without this,
    // `getCarsDetails` is never called and `carsDetailsStore.loading` stays at its initial `true`,
    // leaving the skeleton / spinner stuck on screen. Re-run the non-stripe init on the transition.
    if (prevProps.isStripe && !this.props.isStripe) {
      this.initNonStripe();
    }
  }

  /** Stripe-return-page setup: kick off the express resume and route the result. */
  runStripeResume = (): void => {
    const values = UrlUtils.getValues();
    const carsSessionKey = values[CARS_SESSION_KEY_LABEL] as ISessionKey;
    const resumeSearch = mergeStripeExpressResumeSearch(
      this.props.history.location.search,
      carsSessionKey,
    );
    void (async () => {
      const res = await runStripeExpressResumeFromQuery({
        dispatch: store.dispatch,
        getState: store.getState as () => RootState,
        history: this.props.history,
        pathname: this.props.history.location.pathname,
        search: resumeSearch,
        vertical: 'cars',
      });
      if (res.error && res.isResumeError) {
        // Resume call itself failed → hand off to the regular cars-details page (which has the
        // full booking context) and let it surface the error inline as a normal checkout error.
        // Clear cars-details `loading` *before* the redirect so the reused wrapper instance doesn't
        // briefly flash the skeleton before componentDidUpdate re-runs getCarsDetails.
        store.dispatch(carsDetailsActions.setCarsDetailsLoading(false));
        redirectToCheckoutFromStripeResume(this.props.history, res.error);
        return;
      } else if (res.error) {
        // Pre-resume failure (e.g., missing session) — show the full-page StripeResumeError.
        this.setState({ stripeResumeError: res.error });
      } else if (!res.ran) {
        // No bookingId in the URL (e.g., F5 after a successful booking, once params were stripped).
        this.setState({ stripeNoResume: true });
      }
      store.dispatch(carsDetailsActions.setCarsDetailsLoading(false));
    })();
  };

  /** Regular cars-details page setup: surface any queued stripe-resume error, then load details. */
  initNonStripe = (): void => {
    this.pendingStripeResumeError = consumeStripeResumeErrorFromLocation(this.props.history);
    this.getDetails();
    this.applyPendingStripeResumeError();
  };

  /** Re-apply the resume error onto `errorStripe` after each `getDetails()` so the GeneralMessage
   * banner survives the synchronous `resetCarsState()` inside the cars-details thunk. */
  applyPendingStripeResumeError = (): void => {
    if (this.pendingStripeResumeError) {
      this.props.setCarsErrorStripe(this.pendingStripeResumeError);
    }
  };

  getDetails = () => {
    const values = UrlUtils.getValues();

    if (values[DEAL_HOME_LABEL]) {
      this.setState({ backHomeType: values[DEAL_HOME_LABEL] as string });
    }

    const dealId = values[DEALID_LABEL] ? parseInt(values[DEALID_LABEL] as string) : null;

    this.setState({ dealId });

    this.getCarsDetails(dealId);
  };

  getCarsDetails = (dealId: number) => {
    const { account, user } = this.props.loginStore;

    if (account && user) {
      const values = UrlUtils.getValues();
      const carId = getCarIdFromPath(this.props.history);
      let quote;
      if (values[QUOTE_LABEL]) {
        quote = values[QUOTE_LABEL] as string;
      }
      this.props.getCarsDetails(
        carId,
        values[CARS_SESSION_KEY_LABEL] as ISessionKey,
        dealId,
        quote,
      );
    }
  };

  renderBack = (isMobile?: boolean) => {
    const { dealId, backHomeType, isCheckout } = this.state;

    return (
      <CarsDetailsReturn
        isCheckout={isCheckout}
        isMobile={isMobile}
        dealId={dealId}
        backHomeType={backHomeType}
        onBack={() =>
          this.setState({ isCheckout: false }, () => {
            this.props.setSelectedCarsReviewClientCash(null);
            this.props.setSelectedCarsReviewClientCashStr('');
            this.props.resetCreditCard();
          })
        }
      />
    );
  };

  render(): React.ReactNode {
    const { carsDetailsStore, carsReviewBookStore, loginStore } = this.props;
    const { isCheckout, dealId, backHomeType } = this.state;
    const { car, loading, isServerError, error, selectedExtras } = carsDetailsStore;
    const {
      bookingComplete,
      errorBooking,
      errorStripe,
      stripeLoading,
      selectedCarsReviewClientCash,
      rewardId,
    } = carsReviewBookStore;
    const { account, userWalletData } = loginStore;

    const convertionRate = userWalletData?.convertionRate ?? 1;
    const maxRewardWalletAmount = car?.maxRewardWalletAmount;
    const rewardDeduction = rewardId && maxRewardWalletAmount ? maxRewardWalletAmount : 0;
    const creditLineBookingAmount =
      car?.payNowPrice != null
        ? computeCreditLineChargeTotal({
            payOrBalance: car.payNowPrice,
            clientCashPoints: selectedCarsReviewClientCash,
            conversionRate: convertionRate,
            rewardDeduction,
          })
        : 0;
    const showCreditLineBanner =
      isCheckout &&
      !bookingComplete &&
      !!car &&
      !loading &&
      isCreditLineCheckoutAccount(account);

    /** On stripe-return pages, the wrapper has no car/booking data yet, so render a skeleton until
     * the resume settles (success → CarsBookingConfirmed; failure → early-returned `<StripeResumeError />`). */
    const showStripeSkeleton = !!this.props.isStripe && !bookingComplete;

    /** On stripe-return success the user lands here straight from Stripe — they never clicked
     * "Reserve" in this component, so `isCheckout` is still its initial `false`. Treat the page as
     * if it were in the post-checkout state so:
     *   - the right-side price/location panel is shown,
     *   - the "Reserve" button stays hidden,
     *   - `CarsPriceDetails` switches into its booking-completed layout.
     * The car/booking data feeding that panel comes from the same place as the regular flow —
     * `carsDetailsStore.car`, seeded by `handleProcessBookingComplete(data)` in the resume thunk
     * (which dispatches `setCarsDetails(data)` because the resume response is the same `ICarDetails`
     * shape as `/cars/recheck`). */
    const isStripeConfirmation = !!this.props.isStripe && bookingComplete;
    const effectiveIsCheckout = isCheckout || isStripeConfirmation;

    return (
      <div
        className={`hotel-details-wrapper cars-details-wrapper ${
          bookingComplete ? 'booking-completed' : ''
        }`}
      >
        {this.props.isStripe && this.state.stripeResumeError ? (
          <StripeResumeError />
        ) : this.props.isStripe && this.state.stripeNoResume ? (
          <StripeResumeError mode="go-to-search" />
        ) : isServerError ? (
          <CarsDetailsServerError />
        ) : (
          <>
            {!this.props.isStripe && this.renderBack()}

            {(loading || showStripeSkeleton) ? <SkeletonReviewBook skipRightTop /> : null}

            {!loading && car && isEmpty(error) ? (
              <div className="hotel-details-wrapper__wrapper">
                {this.renderBack(true)}

                {!bookingComplete && (!isEmpty(errorBooking) || !isEmpty(errorStripe)) && (
                  <GeneralMessage
                    type={MessageType.Failure}
                    message={!isEmpty(errorBooking) ? errorBooking : errorStripe}
                  ></GeneralMessage>
                )}

                {showCreditLineBanner ? (
                  <CreditLineCheckoutPageBanner
                    bookingAmount={creditLineBookingAmount}
                    currency={car.currency}
                  />
                ) : null}

                <div className="cars-details-wrapper__content">
                  <div className="cars-details-wrapper__content-left">
                    {/* The form has nothing to fill in on a stripe-return confirmation — the booking
                        already completed on the Stripe side — so skip it and just show the confirmation. */}
                    {!isStripeConfirmation && (
                      <CarsDetails
                        overviewRef={this.overviewRef}
                        paymentRef={this.paymentRef}
                        driverRef={this.driverRef}
                        emailConfirmationRef={this.emailConfirmationRef}
                        reserveRef={this.reserveRef}
                        locationRef={this.locationRef}
                        policiesRef={this.policiesRef}
                        extrasRef={this.extrasRef}
                        paymentFormRef={this.paymentFormRef}
                        driverFormRef={this.driverFormRef}
                        emailFormRef={this.emailFormRef}
                        billingFormRef={this.billingFormRef}
                        isCheckout={isCheckout}
                        dealId={dealId}
                        backHomeType={backHomeType}
                      />
                    )}

                    <CarsBookingConfirmed />
                  </div>

                  <div className="cars-details-wrapper__content-right">
                    {/* Pick-up & drop-off card on top of the right panel. Visible during the regular
                        checkout flow and also on the stripe-return confirmation — that page never
                        went through the local "Reserve" click, so `isCheckout` stays false and the
                        section would otherwise be missing for stripe users. Hidden on the normal
                        post-booking flow, which keeps showing it inside `CarsBookingConfirmed`. */}
                    {(effectiveIsCheckout && !bookingComplete) || isStripeConfirmation ? (
                      <div
                        className="cars-details-wrapper__price-container"
                        style={{ marginBottom: '15px' }}
                      >
                        <CarsLocations car={car} isCheckout />
                      </div>
                    ) : null}

                    <CarsPriceDetails
                      car={car}
                      hideReserve={effectiveIsCheckout}
                      selectedExtras={selectedExtras}
                      onReserve={() => this.setState({ isCheckout: true })}
                      isCheckout={effectiveIsCheckout}
                    />
                  </div>
                </div>

                {/* The section belongs on the confirmation page. `CROSS_SELL_AT_CHECKOUT` also renders
                    it before completion so it can be exercised without paying; off in every env. */}
                {(bookingComplete || (CROSS_SELL_AT_CHECKOUT && effectiveIsCheckout)) && (
                  <CrossSellSection
                    origin="car"
                    carLocations={getCarCrossSellLocations(this.props.carsLocationsStore)}
                    checkIn={this.props.carsDatesStore?.startDate}
                    checkOut={this.props.carsDatesStore?.endDate}
                    currency={car?.currency}
                  />
                )}
              </div>
            ) : null}

            {!loading && !isEmpty(error) ? (
              BookingErrorsEnum.RoomsUnavailable === error ? (
                <AvailabilityStatusModal />
              ) : (
                <CustomErrorPage message={error} isCars />
              )
            ) : null}
          </>
        )}
        <GlobalLoading isVisible={stripeLoading}/>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    carsDetailsStore: state.carsDetailsStore,
    loginStore: state.loginStore,
    carsReviewBookStore: state.carsReviewBookStore,
    carsLocationsStore: state.carsLocationsStore,
    carsDatesStore: state.carsDatesStore,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, Action>): IMapDispatchToProps => ({
  getCarsDetails: (numberId: string, sessionKey: ISessionKey | undefined, dealId: number, quote: string) => {
    dispatch(getCarsDetails(numberId, sessionKey, dealId, quote));
  },
  setSelectedCarsReviewClientCash: (number: number) => {
    dispatch(setSelectedCarsReviewClientCash(number));
  },
  setSelectedCarsReviewClientCashStr: (number: string) => {
    dispatch(setSelectedCarsReviewClientCashStr(number));
  },
  setCarsErrorStripe: (message: string) => {
    dispatch(setCarsErrorStripe(message));
  },
  resetCreditCard: () => {
    dispatch(resetCreditCard());
  },
});

export const CarsDetailsWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(CarsDetailsWrapperComponent));

